import React, { Fragment, useEffect } from 'react';
import { Cell, Column, Row, useExpanded, useTable } from 'react-table';

export function processStringOrFunction<F extends Function>(
  v: string | undefined | F,
  applier: (f: F) => string
): string {
  let result = '';
  if (v) {
    if (typeof v === 'string') {
      result = v;
    } else if (typeof v === 'function') {
      result = applier(v);
    }
  }
  return result;
}

type TableCellProps = { cell: Cell; cellClassName: string; rowIndex: number };

const TableCell = ({ cell, cellClassName, rowIndex }: TableCellProps) => {
  return (
    <td
      {...cell.getCellProps({
        style: {
          minWidth: cell.column.minWidth,
          width: cell.column.width,
          maxWidth: cell.column.maxWidth,
        },
      })}
      className={cellClassName}
    >
      {cell.render('Cell', { index: rowIndex })}
    </td>
  );
};

type TableRowProps = {
  allRowProps: any;
  rowClassName: string;
  row: Row;
  rowIndex: number;
  tableBodyCellClassName?: string | ((row: Row, rowIndex: number, cellIndex: number) => string);
};

const TableRow = ({ allRowProps, rowClassName, row, rowIndex, tableBodyCellClassName }: TableRowProps) => {
  return (
    <tr {...allRowProps} className={rowClassName}>
      {row.cells.map((cell, cellIndex) => {
        const cellClassName = processStringOrFunction(tableBodyCellClassName, (f) => f(row, rowIndex, cellIndex));

        const cellProps = cell.getCellProps({
          style: {
            minWidth: cell.column.minWidth,
            width: cell.column.width,
            maxWidth: cell.column.maxWidth,
          },
        });
        return <TableCell key={cellProps.key} cell={cell} rowIndex={rowIndex} cellClassName={cellClassName} />;
      })}
    </tr>
  );
};

export type BaseTableProps = {
  tableClassName?: string;
  tableHeaderClassName?: string;
  tableHeaderRowClassName?: string;
  tableHeaderCellClassName?: string;
  tableBodyClassName?: string;
  tableBodyRowClassName?: string | ((row: Row, index: number) => string);
  tableBodyCellClassName?: string | ((row: Row, rowIndex: number, cellIndex: number) => string);
  tableBodyRowSubComponentClassName?: string | ((row: Row, index: number) => string);
  hideHeaders?: boolean;
  onRowClick?: (row: Row, index: number) => any;
  columns: Column<{}>[];
  data: {}[];
  expandRowsAtIndexes?: number[];
  forceExpandByIndex?: boolean;
  expandOnlySingleRow?: boolean;
  expandAllRowsByDefault?: boolean;
  renderRowSubComponent?: ({ row, index }: { row: Row; index: number }) => JSX.Element | null;
  onToggleExpandedRow?: (row?: Row) => any;
};

export const BaseTable = ({
  tableClassName = '',
  tableHeaderClassName = '',
  tableHeaderRowClassName = '',
  tableHeaderCellClassName = '',
  tableBodyClassName = '',
  tableBodyRowClassName = '',
  tableBodyCellClassName = '',
  tableBodyRowSubComponentClassName = '',
  hideHeaders = false,
  onRowClick,
  columns,
  data,
  expandRowsAtIndexes = [],
  forceExpandByIndex = false,
  expandOnlySingleRow = false,
  expandAllRowsByDefault = false,
  onToggleExpandedRow,
  renderRowSubComponent,
}: BaseTableProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    state,
    // @ts-ignore
    toggleAllRowsExpanded,
  } = useTable(
    {
      columns,
      data,
      ...(expandOnlySingleRow && {
        stateReducer: (newState, action, prevState) => {
          if (action.type === 'toggleRowExpanded') {
            // @ts-ignore
            return { ...prevState, ...{ expanded: { [action.id]: !prevState?.expanded?.[action.id] } } };
          }
          return newState;
        },
      }),
    },
    useExpanded
  );

  useEffect(() => {
    if (expandAllRowsByDefault) {
      toggleAllRowsExpanded(true);
    }
  }, []);

  useEffect(() => {
    // @ts-ignore
    const expandedState = state.expanded as Record<string, boolean | undefined>;
    const isAnyExpanded = Object.entries(expandedState).find(([k, v]) => !!v);
    if (isAnyExpanded) {
      onToggleExpandedRow && onToggleExpandedRow(rows.find((row) => row.id === isAnyExpanded[0]));
    } else {
      onToggleExpandedRow && onToggleExpandedRow(undefined);
    }
    // @ts-ignore
  }, [state.expanded]);

  return (
    <table id="journey-all-content" {...getTableProps()} className={tableClassName}>
      <thead className={tableHeaderClassName}>
        {!hideHeaders &&
          headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} className={tableHeaderRowClassName}>
              {headerGroup.headers.map((column, ci) => (
                <th
                  {...column.getHeaderProps({
                    style: { minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth },
                  })}
                  className={tableHeaderCellClassName}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
      </thead>
      <tbody {...getTableBodyProps()} className={tableBodyClassName}>
        {rows.map((row, rowIndex) => {
          prepareRow(row);
          const allRowProps = { ...row.getRowProps(), ...(onRowClick && { onClick: () => onRowClick(row, rowIndex) }) };

          const rowClassName = processStringOrFunction(tableBodyRowClassName, (f) => f(row, rowIndex));

          const rowSubComponentClassName = processStringOrFunction(tableBodyRowSubComponentClassName, (f) =>
            f(row, rowIndex)
          );

          const expandRow =
            (expandRowsAtIndexes && expandRowsAtIndexes.includes(rowIndex)) ||
            // @ts-ignore
            (!forceExpandByIndex && row.isExpanded);

          const renderedRow = renderRowSubComponent && renderRowSubComponent({ row, index: rowIndex });

          return (
            <Fragment key={allRowProps.key}>
              <TableRow
                allRowProps={allRowProps}
                rowClassName={rowClassName}
                rowIndex={rowIndex}
                tableBodyCellClassName={tableBodyCellClassName}
                row={row}
              />
              {expandRow && renderedRow && (
                <tr key={`subcomponent-${rowIndex}`}>
                  <td colSpan={visibleColumns.length} className='relative px-0 w-full'>
                    <div className={rowSubComponentClassName}>{renderedRow}</div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};
